import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Save, CalendarDays, Clock, Flag, Users, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import CollaboratorPicker from './CollaboratorPicker';
import { API, SERVICES, PRIORITIES, UNIT_OPTIONS, STATIONS, stationLabel, newRequestId } from '../../utils/kitchenTasks';
import { formatDateLongFR } from '../../utils/dateFormat';
import { formatApiError } from '../../services/api';

const fieldCls = 'h-11 rounded-xl bg-gray-100 border-gray-200 text-gray-900';

const UnitSelect = ({ value, onChange, testId }) => (
  <Select value={value || '__none'} onValueChange={(v) => onChange(v === '__none' ? '' : v)}>
    <SelectTrigger className={`${fieldCls} w-full`} data-testid={testId}><SelectValue placeholder="Unité" /></SelectTrigger>
    <SelectContent className="bg-white border-gray-200">
      <SelectItem value="__none">—</SelectItem>
      {UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
    </SelectContent>
  </Select>
);

// mode: 'bulk' (items = selected prep items) | 'free' (single task)
const TaskFormDialog = ({ open, onOpenChange, mode = 'bulk', items = [], defaultDate, collaborators = [], onSaved }) => {
  const [taskDate, setTaskDate] = useState(defaultDate || '');
  const [service, setService] = useState('midi');
  const [dueTime, setDueTime] = useState('');
  const [priority, setPriority] = useState('normale');
  const [assigned, setAssigned] = useState([]);
  const [description, setDescription] = useState('');
  const [applyToAll, setApplyToAll] = useState(true);
  const [rows, setRows] = useState([]);
  const [free, setFree] = useState({ title: '', category_name: '', planned_quantity: '', unit: '' });
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);
  const requestIdRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTaskDate(defaultDate || '');
    setService('midi'); setDueTime(''); setPriority('normale'); setAssigned([]); setDescription('');
    setApplyToAll(true); setExpanded({}); setSaving(false);
    setFree({ title: '', category_name: '', planned_quantity: '', unit: '' });
    setRows(items.map(it => ({ key: it.id, title: it.name, category_name: it.station, template_id: it.id, planned_quantity: '', unit: it.unit || '', assigned_user_ids: null, description: '' })));
    requestIdRef.current = newRequestId();
  }, [open, defaultDate, items]);

  const updateRow = (key, field, value) => setRows(prev => prev.map(r => (r.key === key ? { ...r, [field]: value } : r)));
  const count = mode === 'bulk' ? rows.length : 1;

  const handleSubmit = async () => {
    if (saving) return;
    if (!taskDate) { toast.error('La date de réalisation est obligatoire'); return; }
    if (!service) { toast.error('Le service est obligatoire'); return; }
    if (mode === 'free' && !free.title.trim()) { toast.error('Le nom de la tâche est obligatoire'); return; }
    setSaving(true);
    try {
      let created;
      if (mode === 'bulk') {
        const res = await axios.post(`${API}/kitchen-tasks/bulk`, {
          task_date: taskDate, service, due_time: dueTime || null, priority, description, assigned_user_ids: assigned,
          apply_common_to_all: applyToAll, client_request_id: requestIdRef.current,
          items: rows.map(r => ({
            title: r.title, category_name: r.category_name, template_id: r.template_id,
            planned_quantity: r.planned_quantity === '' ? null : parseFloat(r.planned_quantity), unit: r.unit,
            assigned_user_ids: applyToAll ? null : r.assigned_user_ids, description: r.description || null,
          })),
        });
        created = res.data.data;
      } else {
        const res = await axios.post(`${API}/kitchen-tasks`, {
          title: free.title.trim(), category_name: free.category_name, task_date: taskDate, service, due_time: dueTime || null,
          planned_quantity: free.planned_quantity === '' ? null : parseFloat(free.planned_quantity), unit: free.unit,
          priority, assigned_user_ids: assigned, description, client_request_id: requestIdRef.current,
        });
        created = [res.data.data];
      }
      const n = created.length;
      toast.success(`${n} tâche${n > 1 ? 's ont' : ' a'} été enregistrée${n > 1 ? 's' : ''} pour le ${formatDateLongFR(taskDate)}.`);
      onSaved?.(created, taskDate);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="task-form-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-heading text-xl text-gray-900">
            {mode === 'bulk' ? `Paramétrer ${count} tâche${count > 1 ? 's' : ''}` : 'Nouvelle tâche'}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            {mode === 'bulk' ? 'Paramètres communs, puis détails par mise en place.' : 'Créer une tâche qui ne figure pas dans les listes.'}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-28 space-y-5">
          {mode === 'free' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-gray-700">Nom de la tâche *</Label>
                <Input value={free.title} onChange={(e) => setFree({ ...free, title: e.target.value })} placeholder="Ex : Égrainer les grenades" className={`mt-1 ${fieldCls}`} data-testid="free-title-input" autoFocus />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-gray-700">Catégorie</Label>
                <Select value={free.category_name || '__none'} onValueChange={(v) => setFree({ ...free, category_name: v === '__none' ? '' : v })}>
                  <SelectTrigger className={`mt-1 ${fieldCls}`} data-testid="free-category-select"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="__none">Sans catégorie</SelectItem>
                    {STATIONS.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-700">Quantité</Label>
                <Input type="number" inputMode="decimal" min="0" step="any" value={free.planned_quantity} onChange={(e) => setFree({ ...free, planned_quantity: e.target.value })} className={`mt-1 ${fieldCls}`} data-testid="free-quantity-input" />
              </div>
              <div>
                <Label className="text-gray-700">Unité</Label>
                <div className="mt-1"><UnitSelect value={free.unit} onChange={(v) => setFree({ ...free, unit: v })} testId="free-unit-select" /></div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-700 flex items-center gap-1.5"><CalendarDays size={14} />Date de réalisation *</Label>
              <input type="date" value={taskDate} onChange={(e) => setTaskDate(e.target.value)} required
                className={`mt-1 block w-full px-3 text-sm border ${fieldCls} focus:outline-none focus:ring-2 focus:ring-neon-pink/50`} data-testid="task-date-input" />
            </div>
            <div>
              <Label className="text-gray-700">Service *</Label>
              <Select value={service} onValueChange={setService}>
                <SelectTrigger className={`mt-1 ${fieldCls}`} data-testid="task-service-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {SERVICES.map(s => <SelectItem key={s.value} value={s.value} data-testid={`service-option-${s.value}`}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-700 flex items-center gap-1.5"><Clock size={14} />Heure limite</Label>
              <input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)}
                className={`mt-1 block w-full px-3 text-sm border ${fieldCls} focus:outline-none focus:ring-2 focus:ring-neon-pink/50`} data-testid="task-due-time-input" />
            </div>
            <div>
              <Label className="text-gray-700 flex items-center gap-1.5"><Flag size={14} />Priorité</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className={`mt-1 ${fieldCls}`} data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {PRIORITIES.map(p => <SelectItem key={p.value} value={p.value} data-testid={`priority-option-${p.value}`}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-gray-700 flex items-center gap-1.5 mb-2"><Users size={14} />Collaborateur(s)</Label>
            <CollaboratorPicker collaborators={collaborators} value={assigned} onChange={setAssigned} testIdPrefix="task-collab" />
            {assigned.length === 0 && <p className="text-xs text-gray-500 mt-2">Sans collaborateur, les tâches seront enregistrées comme « Non affectées ».</p>}
          </div>

          <div>
            <Label className="text-gray-700 flex items-center gap-1.5"><MessageSquare size={14} />Consigne {mode === 'bulk' ? 'générale' : ''}</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Consigne du chef…" className="mt-1 rounded-xl bg-gray-100 border-gray-200 text-gray-900" data-testid="task-description-input" />
          </div>

          {mode === 'bulk' && (
            <>
              <label className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 cursor-pointer">
                <span className="text-sm text-gray-800">Appliquer le même collaborateur, la même priorité et la même heure à toutes les tâches.</span>
                <Switch checked={applyToAll} onCheckedChange={setApplyToAll} data-testid="apply-to-all-switch" />
              </label>

              <div className="space-y-2" data-testid="bulk-rows">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Détail par mise en place</p>
                {rows.map((r, idx) => (
                  <div key={r.key} className="border border-gray-200 rounded-xl p-3 space-y-2" data-testid={`bulk-row-${idx}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm truncate">{r.title}</p>
                        <p className="text-xs text-gray-500">{stationLabel(r.category_name)}</p>
                      </div>
                      <div className="w-24">
                        <Input type="number" inputMode="decimal" min="0" step="any" placeholder="Qté" value={r.planned_quantity} onChange={(e) => updateRow(r.key, 'planned_quantity', e.target.value)} className={fieldCls} data-testid={`bulk-qty-${idx}`} />
                      </div>
                      <div className="w-24">
                        <UnitSelect value={r.unit} onChange={(v) => updateRow(r.key, 'unit', v)} testId={`bulk-unit-${idx}`} />
                      </div>
                    </div>
                    <button type="button" onClick={() => setExpanded(e => ({ ...e, [r.key]: !e[r.key] }))} className="text-xs text-neon-pink font-medium flex items-center gap-1 min-h-[32px]" data-testid={`bulk-row-toggle-${idx}`}>
                      {expanded[r.key] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}Collaborateur et consigne spécifiques
                    </button>
                    {expanded[r.key] && (
                      <div className="space-y-2 pt-1">
                        {!applyToAll ? (
                          <CollaboratorPicker compact collaborators={collaborators} value={r.assigned_user_ids || []} onChange={(v) => updateRow(r.key, 'assigned_user_ids', v)} testIdPrefix={`bulk-collab-${idx}`} />
                        ) : (
                          <p className="text-xs text-gray-500">Désactivez « Appliquer à toutes les tâches » pour choisir un collaborateur spécifique.</p>
                        )}
                        <Input value={r.description} onChange={(e) => updateRow(r.key, 'description', e.target.value)} placeholder="Consigne spécifique (facultatif)" className={fieldCls} data-testid={`bulk-note-${idx}`} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-3 flex gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="flex-1 h-12 rounded-full border-gray-300 text-gray-700" data-testid="task-form-cancel-btn">Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving} className="flex-1 h-12 rounded-full bg-neon-pink hover:bg-neon-pink/90 text-gray-900 font-semibold shadow-neon-pink" data-testid="task-form-submit-btn">
            {saving ? <Loader2 size={18} className="animate-spin mr-2" /> : <Save size={18} className="mr-2" />}
            Enregistrer {count} tâche{count > 1 ? 's' : ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TaskFormDialog;
